import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '../../../components/loading/loading';
import { useSubscribeToProduct } from '../../../queries/ats/me/mutations';
import { useProducts, useSubscribedProducts } from '../../../queries/ats/me/products/hooks';
import { Product } from '../../../queries/ats/me/products/types';
import { Source } from '../../../queries/ats/titles/types/sources';
import { ascending } from '../../../utils/array-utils';
import { FocusedAsset } from '../use-focused-asset';
import { PurchaseProductItem } from './purchase-product-item';
import css from './purchase-product-modal.module.scss';
import { PurchaseSuccess } from './purchase-success';
import { getPurchaseModalTitleName } from './utils';

const productKey = (product: Product) => `${product.type}-${product.resolution}`;

function dedupeProducts(products: Product[]) {
  const dedupedProducts = products.reduce((map, product) => {
    const key = productKey(product);
    const seenProduct = map.get(key);

    if (seenProduct !== undefined && seenProduct.price <= product.price) {
      return map.set(key, seenProduct);
    }

    return map.set(key, product);
  }, new Map<string, Product>());

  return Array.from(dedupedProducts.values());
}

interface PurchaseProductModalContentProps {
  asset: FocusedAsset;
  source: Source;
}

export function PurchaseProductModalContent({ asset, source }: PurchaseProductModalContentProps) {
  const {
    data: products,
    isLoading: isProductsLoading,
    error: productsError,
  } = useProducts(source.platformTitleId, {
    select: (data) => dedupeProducts(data),
  });

  const {
    data: subscribedProducts,
    isLoading: isSubscribedProductsLoading,
    error: subscribedProductsError,
  } = useSubscribedProducts();

  const { mutate, error: purchaseError, isLoading: isPurchaseLoading, isSuccess } = useSubscribeToProduct();

  const error = productsError || subscribedProductsError || purchaseError;
  const isLoading = isProductsLoading || isSubscribedProductsLoading;

  const title = getPurchaseModalTitleName(asset);

  if (error !== null) {
    throw error;
  }

  if (isLoading) {
    return (
      <main className={css.container}>
        <h2 className={css.title}>{title}</h2>
        <div className={css.buttons}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className={css.button} />
          ))}
        </div>
      </main>
    );
  }

  if (products === undefined) {
    throw new Error(`Could not find products for ${source.platformTitleId}.`);
  }

  const sortedProducts = products.sort(ascending((x) => x.price));
  const subscribedProduct =
    subscribedProducts !== undefined
      ? subscribedProducts.find((product) => product.platformTitleId === source.platformTitleId)
      : undefined;

  const originalProduct = sortedProducts.find((product) => product.id === subscribedProduct?.id);

  if (isSuccess) {
    return (
      <main className={css.container}>
        <h2 className={css.title}>{title}</h2>
        {subscribedProduct === undefined ? (
          <Loading classNames={{ spinner: css.loading }} />
        ) : (
          <PurchaseSuccess
            asset={asset}
            product={{
              ...subscribedProduct,
              // ATS returns 0 for all prices from subscribed products, this is a bug
              price: originalProduct?.price ?? subscribedProduct.price,
            }}
          />
        )}
      </main>
    );
  }

  return (
    <main className={css.container}>
      <h2 className={css.title}>{title}</h2>

      {isPurchaseLoading ? (
        <Loading classNames={{ spinner: css.loading }} />
      ) : (
        <Fragment>
          <div className={css.buttons}>
            {sortedProducts.map((product) => (
              <PurchaseProductItem
                key={product.id}
                product={product}
                platformTitleId={source.platformTitleId}
                purchase={mutate}
              />
            ))}
          </div>
          <ConditionsText />
        </Fragment>
      )}
    </main>
  );
}

function ConditionsText() {
  const { t } = useTranslation();

  return (
    <span className={css.conditionsText}>
      {t('tos1')}
      <a className={css.termsLink} href={`${t('tos link')}`} target="_blank" rel="noreferrer">
        {t('tos')}
      </a>
      {'. '}
      {t('tos2')}
    </span>
  );
}
